<template>
  <b-card no-body class="task-card" :class="{ 'dropdown-open': dropdownOpen }" @click="handleCardClick">
    <b-card-body class="p-3">
      <!-- Header with title, avatar, and three-dot menu -->
      <div class="d-flex justify-content-between align-items-start mb-2">
        <!-- Avatar on the left -->
        <b-avatar src="https://pickaface.net/gallery/avatar/unr_sample_161118_2054_ynlrg.png" size="sm" class="mr-2 avatar flex-shrink-0"></b-avatar>

        <!-- Task title -->
        <b-card-text class="task-title flex-grow-1 mr-2">
          {{ task.name }}
        </b-card-text>

        <!-- Three-dot menu on the right -->
        <b-dropdown 
          variant="link" 
          no-caret 
          right 
          class="task-dropdown flex-shrink-0" 
          boundary="viewport" 
          :popper-opts="{ placement: 'bottom-end', modifiers: { preventOverflow: { boundariesElement: 'viewport' } } }"
          @show="dropdownOpen = true"
          @hide="dropdownOpen = false">
          <template #button-content>
            <i class="fa fa-ellipsis-v task-menu-icon"></i>
          </template>

          <!-- Edit option -->
          <b-dropdown-item @click.stop="handleEdit">
            <i class="fa fa-edit mr-2"></i> Edit
          </b-dropdown-item>

          <!-- Move option -->
          <b-dropdown-item @click.stop="showMoveModal = true">
            <i class="fa fa-arrows-alt mr-2"></i> Move to...
          </b-dropdown-item>

          <b-dropdown-divider></b-dropdown-divider>

          <!-- Delete option -->
          <b-dropdown-item @click.stop="handleDelete">
            <i class="fa fa-trash mr-2"></i> Delete
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <!-- Task details preview. Parse into text/mention parts so @-mentions
           render as blue pills that stand out. We DO NOT pass raw HTML to
           vue-truncate-collapsed with type="html" — that locks the render
           thread walking the nested <at>-picker span soup. Full HTML still
           renders in the Edit panel's contenteditable. -->
      <div v-if="detailsPreviewParts.length" class="task-details">
        <template v-for="(part, i) in detailsPreviewParts">
          <span v-if="part.type === 'mention'" :key="i" class="task-mention">{{ part.text }}</span>
          <span v-else :key="i">{{ part.text }}</span>
        </template>
      </div>

      <!-- Footer with date and badge -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <small class="task-date">{{ formatTaskDate(task.date) }}</small>
        <div class="tag-container">
          <span v-for="(tag, index) in task.tags" :key="index" class="task-tag">{{ tag.name }}</span>
        </div>
      </div>
    </b-card-body>

    <!-- Move Task Modal -->
    <b-modal v-model="showMoveModal" title="Move Task" size="sm" @ok="handleMoveConfirm" @cancel="selectedColumnId = null">
      <p class="mb-3">Move "<strong>{{ task.name }}</strong>" to:</p>
      <b-form-select v-model="selectedColumnId" :options="columnOptions" class="mb-3">
        <template #first>
          <option :value="null" disabled>Select a column</option>
        </template>
      </b-form-select>
    </b-modal>

    <!-- Edit Task is now a slide-out panel (EditTaskPanel.vue), opened via
         $showPanel in openEditModal(). Moved out of b-modal so the <at>
         picker's caret-positioning math works (b-modal's position:fixed
         breaks getOffset). -->
  </b-card>
</template>

<script>
import Badge from "./Badge.vue";
import mixin from '../../mixin.js';
import EditTaskPanel from './components/EditTaskPanel.vue';
import { toPreviewParts } from '@/utils/kanbanDetailsPreview';

export default {
  components: {
    Badge,
  },
  mixins: [mixin],
  data() {
    return {
      showMoveModal: false,
      selectedColumnId: null,
      dropdownOpen: false
    };
  },
  props: {
    task: {
      type: Object,
      default: () => ({})
    },
    taskIdx: {
      type: Number
    },
    columnIdx: {
      type: Number
    },
    availableColumns: {
      type: Array,
      default: () => []
    },
    currentColumnId: {
      type: String,
      default: ''
    },
    // Pass only the boardId (string, cheap). The panel reads the full board
    // from the Vuex store itself so we don't create an O(N^2) reactivity
    // tracking graph from passing a big reactive object across N TaskCards.
    boardId: {
      type: String,
      default: '',
    },
  },
  computed: {
    columnOptions() {
      return this.availableColumns
        .filter(col => col.id !== this.currentColumnId)
        .map(col => ({
          value: col.id,
          text: `${col.name} (${col.tasks.length} tasks)`
        }));
    },
    badgeColor() {
      const mappings = {
        Design: "info",
        "Feature Request": "primary",
        Backend: "secondary",
        QA: "success",
        default: "primary"
      };
      return mappings[this.task.type] || mappings.default;
    },

    // Parsed text+mention parts for the card preview. See
    // src/utils/kanbanDetailsPreview.js for the why (freeze fix 2026-04-16).
    detailsPreviewParts() {
      return toPreviewParts(this.task && this.task.details);
    },
  },
  
  methods: {
    formatTaskDate(date) {
      if (!date) {
        return 'No date';
      }
      
      try {
        // Handle Firestore timestamp format
        if (date.seconds) {
          const milliseconds = date.seconds * 1000 + Math.floor(date.nanoseconds / 1000000);
          return this.$moment(milliseconds).format(this.dateFormatStr || 'MM/DD/YYYY');
        }
        
        // Handle regular date
        return this.$moment(date).format(this.dateFormatStr || 'MM/DD/YYYY');
      } catch (error) {
        console.warn('Error formatting task date:', error);
        return 'Invalid date';
      }
    },
    
    handleCardClick(event) {
      // Don't open modal if clicking on dropdown or its children
      if (event.target.closest('.task-dropdown') || event.target.closest('.dropdown-menu')) {
        return;
      }
      
      // Don't open modal if clicking on links or buttons
      if (event.target.tagName === 'A' || event.target.tagName === 'BUTTON' || event.target.closest('button')) {
        return;
      }
      
      this.openEditModal();
    },

    // Edit Task opens as a slide-out panel (EditTaskPanel).
    //
    // The panel OWNS the save (dispatches kanban/updateBoard +
    // writeKanbanNotification internally) to avoid racing with the plugin's
    // close transition — doing the save post-emit was freezing the page.
    openEditModal() {
      this.$showPanel({
        component: EditTaskPanel,
        openOn: 'right',
        width: 720,
        disableBgClick: true,
        disableEscClick: true,
        props: {
          task: this.task,
          columnIdx: this.columnIdx,
          taskIdx: this.taskIdx,
          columnName: this.getColumnName(),
          dateFormatStr: this.dateFormatStr || 'MM/DD/YYYY',
          boardId: this.boardId,
        },
      });
    },

    // Keep the old handleEdit method for backward compatibility with dropdown menu
    handleEdit() {
      this.openEditModal();
    },

    getColumnName() {
      if (this.availableColumns && this.currentColumnId) {
        const column = this.availableColumns.find(col => col.id === this.currentColumnId);
        return column ? column.name : 'Unknown';
      }
      return 'Unknown';
    },
    
    handleDelete() {
      this.$emit('remove-task', this.task, this.columnIdx, this.taskIdx);
    },

    handleMoveConfirm(bvModalEvent) {
      if (!this.selectedColumnId) {
        bvModalEvent.preventDefault();
        return;
      }
      
      const targetColumn = this.availableColumns.find(col => col.id === this.selectedColumnId);
      if (targetColumn) {
        this.$emit('move-task', {
          task: this.task,
          fromColumnIdx: this.columnIdx,
          taskIdx: this.taskIdx,
          targetColumn: targetColumn
        });
      }
      
      // Reset modal state
      this.selectedColumnId = null;
    }
  }
};
</script>

<style scoped>
.task-card {
  border: 1px solid #e8e8ef;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
  background: #fff;
  overflow: visible; /* Allow dropdown to show outside card */
  position: relative;
  z-index: 1;
  cursor: pointer;
}

/* Elevate task card when dropdown is open */
.task-card.dropdown-open {
  z-index: 1250 !important;
}

.task-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  border-color: #d0d5dd;
}

/* Removed left border decoration for cleaner look */

.task-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: #3e4b5b;
  line-height: 1.3;
  margin-bottom: 0;
  letter-spacing: 0.01rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.task-details {
  font-size: 0.85rem;
  color: #6c757d;
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.task-mention {
  display: inline-block;
  padding: 0.05rem 0.45rem;
  margin: 0 0.1rem;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0369a1;
  font-weight: 600;
  font-size: 0.78rem;
  line-height: 1.4;
  white-space: nowrap;
}

.task-date {
  color: #8a94a6;
  font-size: 0.8rem;
}

.tag-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.task-tag {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-left: 0.35rem;
  margin-bottom: 0.35rem;
  background-color: #dbeafe;
  color: #1d4ed8;
  transition: all 0.2s ease;
}

.task-tag:hover {
  opacity: 0.85;
}

.avatar {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease;
}

.avatar:hover {
  transform: scale(1.1);
}

.task-dropdown {
  position: relative;
  z-index: 1200;
}

.task-dropdown.show {
  z-index: 1300; /* Even higher when dropdown is open */
}

/* Ensure the entire task card has higher z-index when dropdown is open */
.task-card:has(.task-dropdown.show) {
  z-index: 1250;
  position: relative;
}

.task-dropdown .dropdown-toggle {
  padding: 0.25rem;
  margin: 0;
  border: none;
  background: none;
  color: #8a94a6;
  transition: all 0.2s ease;
  min-width: 20px;
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.task-dropdown .dropdown-toggle:hover,
.task-dropdown .dropdown-toggle:focus,
.task-dropdown .dropdown-toggle:active {
  color: #007bff !important;
  background: none !important;
  box-shadow: none !important;
  border: none !important;
}

.task-menu-icon {
  font-size: 14px;
  pointer-events: none;
}

.task-dropdown .dropdown-menu {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  min-width: 140px;
  z-index: 1350 !important;
  position: absolute;
  transform: translateZ(0); /* Force hardware acceleration for better layering */
}

.task-dropdown .dropdown-item {
  padding: 0.6rem 1rem;
  transition: background-color 0.2s ease;
  color: #495057;
  font-size: 0.875rem;
}

.task-dropdown .dropdown-item:hover,
.task-dropdown .dropdown-item:focus {
  background-color: rgba(0, 123, 255, 0.07);
  color: #007bff;
}

.task-dropdown .dropdown-item:active {
  background-color: rgba(0, 123, 255, 0.1);
}

.truncate-more {
  color: #007bff;
  font-size: 0.8rem;
  font-weight: 500;
  text-decoration: none;
  margin-top: 0.25rem;
  display: inline-block;
  transition: color 0.2s ease;
}

.truncate-more:hover {
  color: #0056b3;
  text-decoration: underline;
}

.collapsed {
  max-height: 3.6em;
  overflow: hidden;
}

/* Mobile Responsiveness */
@media (max-width: 767.98px) {
  .task-card {
    margin-bottom: 0.75rem;
  }
  
  .task-title {
    font-size: 0.9rem;
    line-height: 1.2;
  }
  
  .task-details {
    font-size: 0.8rem;
  }
  
  .task-date {
    font-size: 0.75rem;
  }
  
  .task-tag {
    font-size: 0.65rem;
    padding: 0.2rem 0.5rem;
    margin-left: 0.25rem;
    margin-bottom: 0.25rem;
  }
  
  .tag-container {
    flex-wrap: wrap;
    gap: 0.25rem;
  }
  
  .avatar {
    width: 24px !important;
    height: 24px !important;
  }
  
  .task-dropdown .dropdown-toggle {
    padding: 0.5rem;
    min-width: 32px;
    min-height: 32px;
  }
  
  .task-menu-icon {
    font-size: 16px;
  }
}

@media (max-width: 575.98px) {
  .task-title {
    font-size: 0.85rem;
  }
  
  .task-details {
    font-size: 0.75rem;
    margin-top: 0.5rem;
  }
}

/* ===== Task Details Panel - Matching TodoDetails Style ===== */
.task-details-panel {
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, sans-serif;
  color: #334155;
  background-color: #f8fafc;
  padding: 1.25rem;
}

.task-details-main {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  padding: 1.25rem;
  margin-bottom: 1rem;
}

.task-details-item {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f1f5f9;
}

.task-details-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.task-content-area {
  margin-bottom: 0.5rem;
}

.task-name-input {
  font-size: 1.125rem;
  font-weight: 600;
  color: #0f172a;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0.75rem;
  background-color: white;
  transition: all 0.15s ease;
}

.task-name-input:focus {
  outline: none;
  border-color: #3abf94;
  box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.2);
}

.detail-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
}

.detail-label {
  width: 120px;
  font-weight: 600;
  color: #475569;
  font-size: 0.875rem;
  padding-top: 0.5rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.detail-label i {
  color: #3abf94;
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.detail-value {
  flex: 1;
  min-width: 0;
  font-size: 0.9375rem;
}

.task-input,
.task-textarea {
  width: 100%;
  font-size: 0.9375rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0.625rem 0.75rem;
  background-color: white;
  color: #334155;
  transition: all 0.15s ease;
}

.task-input:focus,
.task-textarea:focus {
  outline: none;
  border-color: #3abf94;
  box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.2);
}

.task-textarea {
  resize: vertical;
  min-height: 80px;
  line-height: 1.5;
}

/* Status and Date Badges */
.status-badge {
  display: inline-block;
  background-color: #dbeafe;
  color: #1e40af;
  padding: 0.25rem 0.625rem;
  border-radius: 0.25rem;
  font-weight: 500;
  font-size: 0.875rem;
}

.date-badge {
  display: inline-block;
  background-color: #f1f5f9;
  color: #334155;
  padding: 0.25rem 0.625rem;
  border-radius: 0.25rem;
  font-weight: 500;
  font-size: 0.875rem;
}

/* Tags Multiselect - Matching TodoDetails */
.task-tags-select .multiselect__tags {
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background-color: white;
  min-height: 42px;
  padding: 6px 40px 0 8px;
}

.task-tags-select .multiselect__tags:focus-within {
  border-color: #3abf94;
  box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.2);
}

.task-tags-select .multiselect__tag {
  background-color: #3abf94;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 0.25rem;
  padding: 0.375rem 1.75rem 0.375rem 0.625rem;
  margin-bottom: 4px;
  margin-right: 0.375rem;
  position: relative;
  display: inline-flex;
  align-items: center;
}

.task-tags-select .multiselect__tag-icon {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-style: normal;
  line-height: 1;
  border-radius: 0 0.25rem 0.25rem 0;
  transition: background-color 0.15s ease;
}

.task-tags-select .multiselect__tag-icon:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.task-tags-select .multiselect__tag-icon::after {
  content: '×';
  font-size: 1rem;
  color: #fff;
}

.task-tags-select .multiselect__option--highlight {
  background-color: #3abf94;
  color: #fff;
}

.task-tags-select .multiselect__input,
.task-tags-select .multiselect__placeholder {
  font-size: 0.875rem;
  color: #64748b;
}

/* Action Buttons */
.task-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.action-btn {
  font-weight: 500;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  transition: all 0.15s ease;
}

.action-btn-primary {
  background-color: #3abf94;
  border-color: #3abf94;
  color: white;
}

.action-btn-primary:hover {
  background-color: #2d9474;
  border-color: #2d9474;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(58, 191, 148, 0.3);
}

/* Modal Styling Override */
::v-deep .task-details-modal .modal-header {
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.25rem;
  background-color: #f8fafc;
}

::v-deep .task-details-modal .modal-title {
  font-family: 'Inter', 'Segoe UI', Roboto, sans-serif;
  font-weight: 600;
  font-size: 1.125rem;
  color: #0f172a;
}

::v-deep .task-details-modal .modal-content {
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

/* Responsive adjustments */
@media (max-width: 575.98px) {
  .detail-row {
    flex-direction: column;
  }

  .detail-label {
    width: 100%;
    margin-bottom: 0.375rem;
    padding-top: 0;
  }

  .task-actions {
    flex-direction: column-reverse;
  }

  .action-btn {
    width: 100%;
  }
}

</style>